import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { clinicsService, type Patient, type Appointment, type DashboardResponse } from '../../services/clinicsService';

interface ClinicsState {
  dashboardStats: DashboardResponse | null;
  patients: Patient[];
  selectedPatient: Patient | null;
  appointments: Appointment[];
  totalPatients: number;
  totalAppointments: number;
  loading: boolean;
  error: string | null;
}

const initialState: ClinicsState = {
  dashboardStats: null,
  patients: [],
  selectedPatient: null,
  appointments: [],
  totalPatients: 0,
  totalAppointments: 0,
  loading: false,
  error: null,
};

// Thunks
export const fetchDashboardStats = createAsyncThunk(
  'clinics/fetchDashboardStats',
  async (_, { rejectWithValue }) => {
    try {
      const data = await clinicsService.getDashboard();
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Error loading dashboard');
    }
  }
);

export const fetchPatients = createAsyncThunk(
  'clinics/fetchPatients',
  async (params: { page?: number; per_page?: number; search?: string; status?: string; tag_ids?: string } | undefined, { rejectWithValue }) => {
    try {
      const data = await clinicsService.getPatients(params);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Error loading patients');
    }
  }
);

export const fetchPatientById = createAsyncThunk(
  'clinics/fetchPatientById',
  async (id: number, { rejectWithValue }) => {
    try {
      const data = await clinicsService.getPatientById(id);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Error loading patient');
    }
  }
);

export const fetchAppointments = createAsyncThunk(
  'clinics/fetchAppointments',
  async (params: { page?: number; per_page?: number; patient_id?: number; status?: string; tag_ids?: string; date_from?: string; date_to?: string; room_id?: string } | undefined, { rejectWithValue }) => {
    try {
      const data = await clinicsService.getAppointments(params);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Error loading appointments');
    }
  }
);

const clinicsSlice = createSlice({
  name: 'clinics',
  initialState,
  reducers: {
    clearSelectedPatient: (state) => {
      state.selectedPatient = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Dashboard
      .addCase(fetchDashboardStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboardStats = action.payload;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Patients list
      .addCase(fetchPatients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatients.fulfilled, (state, action) => {
        state.loading = false;
        state.patients = action.payload.patients;
        state.totalPatients = action.payload.total;
      })
      .addCase(fetchPatients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Single patient
      .addCase(fetchPatientById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatientById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedPatient = action.payload;
      })
      .addCase(fetchPatientById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Appointments list
      .addCase(fetchAppointments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAppointments.fulfilled, (state, action) => {
        state.loading = false;
        state.appointments = action.payload.appointments;
        state.totalAppointments = action.payload.total;
      })
      .addCase(fetchAppointments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearSelectedPatient, clearError } = clinicsSlice.actions;

export default clinicsSlice.reducer;
